import LocalImages from "assets/images/local-images";
import Content, { ContentNS } from "content/content";
import { ScannerOverlayState } from "features/document-capture/DocumentCapture";
import PrimaryButton from "features/shared/components/buttons/PrimaryButton.styled";
import { MicroHeaderXS } from "features/shared/styles/typography/MicroHeaderXS.styled";
import { Summary } from "features/shared/styles/typography/Summary.styled";
import ScannerDocumentConfig, {
  ScanDocumentSidesMode,
} from "models/scanner-document-config";
import { FC, ReactNode, useEffect, useRef, useState } from "react";

import FlipDocumentAnimation from "./components/FlipDocumentAnimation";
import ImageHint from "./styles/ImageHint.styled";
import ScannerOverlayRoot from "./styles/ScannerOverlayRoot.styled";

interface ScannerOverlayProps {
  scannerState: ScannerOverlayState;
  docScannerConfig: ScannerDocumentConfig;
  startScanning: () => void;
}

const COUNTDOWN_INTERVAL = 1000;
const COUNTDOWN_START = 10;

const ScannerOverlay: FC<ScannerOverlayProps> = ({
  scannerState,
  docScannerConfig,
  startScanning,
}) => {
  const textUI = (s: string) => Content.shared.t(s, { ns: ContentNS.ui });

  const [countdown, setCountdown] = useState(COUNTDOWN_START);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (
      timeoutRef.current &&
      scannerState !== ScannerOverlayState.BeforeScanningBackSide
    ) {
      return;
    }

    // When scanning multi-side documents the "start scanning" countdown  needs to be reset
    // to display correct countdown on scanner overlay
    if (scannerState === ScannerOverlayState.BeforeScanningBackSide) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCountdown(COUNTDOWN_START);
    }

    timeoutRef.current = setInterval(() => {
      setCountdown((prevCountdown) => {
        return prevCountdown - 1;
      });
    }, COUNTDOWN_INTERVAL);
  }, [scannerState]);

  useEffect(() => {
    if (countdown === 0) {
      if (timeoutRef.current) {
        clearInterval(timeoutRef.current);
      }
      startScanning();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown]);

  const helperImage = (): ReactNode => {
    // 1) if multiside and before scanning back - show doc flip animation
    if (
      docScannerConfig.mode === ScanDocumentSidesMode.MultiSide &&
      scannerState === ScannerOverlayState.BeforeScanningBackSide
    ) {
      return <FlipDocumentAnimation />;
    }

    let imageSrc = "";
    let imageAlt = "";

    // 2) case when we have both Data Page and one Card side
    // - need ambiguous icon, as "back side" is confusing for when scanning a passport
    if (
      docScannerConfig.mode === ScanDocumentSidesMode.CardBackOrDataPage ||
      docScannerConfig.mode === ScanDocumentSidesMode.CardFrontOrDataPage
    ) {
      if (
        scannerState === ScannerOverlayState.ScanningFrontSide ||
        scannerState === ScannerOverlayState.BeforeScanningFrontSide
      ) {
        imageAlt = textUI("scanner-overlay.front-icon-alt-ambiguous");
      } else {
        imageAlt = textUI("scanner-overlay.back-icon-alt-ambiguous");
      }

      imageSrc =
        docScannerConfig.mode === ScanDocumentSidesMode.CardBackOrDataPage
          ? LocalImages.shared.images.icons.scannerOverlayDocBackMrz
          : LocalImages.shared.images.icons.scannerOverlayDocFrontMrz;

      return <img src={imageSrc} alt={imageAlt} />;
    }

    // 3) else
    if (
      scannerState === ScannerOverlayState.ScanningFrontSide ||
      scannerState === ScannerOverlayState.BeforeScanningFrontSide
    ) {
      imageAlt = textUI("scanner-overlay.front-icon-alt");
      imageSrc = LocalImages.shared.images.icons.scannerOverlayDocFrontNoMrz;
    } else {
      imageAlt = textUI("scanner-overlay.back-icon-alt");
      imageSrc = LocalImages.shared.images.icons.scannerOverlayDocBackMrz;
    }

    return <img src={imageSrc} alt={imageAlt} />;
  };

  const helperIconText = (): string | null => {
    if (
      docScannerConfig.mode === ScanDocumentSidesMode.CardBackOrDataPage ||
      docScannerConfig.mode === ScanDocumentSidesMode.CardFrontOrDataPage
    ) {
      return null;
    }

    return scannerState === ScannerOverlayState.ScanningFrontSide
      ? textUI("scanner-overlay.front")
      : textUI("scanner-overlay.back");
  };

  if (
    scannerState === ScannerOverlayState.ScanningDataPage ||
    scannerState === ScannerOverlayState.ScanningNotSupported
  ) {
    return null;
  }

  if (
    [
      ScannerOverlayState.BeforeScanningFrontSide,
      ScannerOverlayState.BeforeScanningBackSide,
    ].includes(scannerState)
  ) {
    const postfix =
      docScannerConfig.mode === ScanDocumentSidesMode.CardBackOrDataPage ||
      docScannerConfig.mode === ScanDocumentSidesMode.CardFrontOrDataPage
        ? "-ambiguous"
        : "";
    return (
      <ScannerOverlayRoot>
        <div>
          <Summary>
            {scannerState === ScannerOverlayState.BeforeScanningFrontSide ||
            [
              ScanDocumentSidesMode.CardBackOrDataPage,
              ScanDocumentSidesMode.OnlyCardBack,
            ].includes(docScannerConfig.mode)
              ? textUI("scanner-overlay.present" + postfix)
              : textUI("scanner-overlay.present-back" + postfix)}
          </Summary>
          &nbsp;
          <Summary className="bolded">
            {scannerState === ScannerOverlayState.BeforeScanningFrontSide
              ? textUI("scanner-overlay.front-side" + postfix)
              : textUI("scanner-overlay.back-side" + postfix)}
          </Summary>
        </div>

        {helperImage()}

        <PrimaryButton
          onClick={() => {
            if (timeoutRef.current) {
              clearInterval(timeoutRef.current);
            }
            startScanning();
          }}
        >
          {`${textUI("scanner-overlay.start-scanning")} (${countdown})`}
        </PrimaryButton>
      </ScannerOverlayRoot>
    );
  }

  if (docScannerConfig.mode !== ScanDocumentSidesMode.OnlyDataPage) {
    const iconText = helperIconText();
    return (
      <ImageHint className="doc-image-container">
        {helperImage()}
        {iconText ? <MicroHeaderXS>{iconText}</MicroHeaderXS> : null}
      </ImageHint>
    );
  }

  return null;
};

export default ScannerOverlay;
